import { AppConfig } from '@ltvco/refresh-lib/ctx';
import { useCallback, useContext, useEffect, useRef } from 'react';

import {
  AutosuggestionContainer,
  AutosuggestionIcon,
  AutosuggestionItem,
  AutosuggestionList,
  AutosuggestionTitle,
} from 'components/ChatBV/components';
import {
  CHATBV_GA4_EVENTS,
  PLACEHOLDER_COLLECTION,
} from 'components/ChatBV/constants';
import { PlaceholderItem } from 'components/ChatBV/types/generalTypes';

interface AutosuggestionPromptsProps {
  isVisible: boolean;
  onSelectPrompt: (
    suggestionMainText: string,
    suggestionExampleText: string,
    type: string
  ) => void;
  onClose: () => void;
}

export const AutosuggestionPrompts = ({
  isVisible,
  onSelectPrompt,
  onClose,
}: AutosuggestionPromptsProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { trackEventGA4 } = useContext(AppConfig);

  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    },
    [onClose]
  );

  useEffect(() => {
    if (isVisible) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isVisible, handleClickOutside]);

  const handlePromptClick = (item: PlaceholderItem) => {
    trackEventGA4({
      event_name: CHATBV_GA4_EVENTS.name,
      type: CHATBV_GA4_EVENTS.suggestion.type,
      subtype: CHATBV_GA4_EVENTS.suggestion.subTypes.dropdown_suggestion_click,
    });

    onSelectPrompt(
      item.suggestionMainText,
      item.suggestionExampleText,
      item.searchType
    );
    onClose();
  };

  return (
    <AutosuggestionContainer
      ref={containerRef}
      className={isVisible ? 'visible' : ''}
    >
      <AutosuggestionTitle>You might ask</AutosuggestionTitle>
      <AutosuggestionList>
        {PLACEHOLDER_COLLECTION.map(
          (item, index) =>
            item?.suggestionTitle && (
              <AutosuggestionItem
                key={index}
                onClick={() => handlePromptClick(item)}
              >
                <AutosuggestionIcon />
                {item.suggestionTitle}
              </AutosuggestionItem>
            )
        )}
      </AutosuggestionList>
    </AutosuggestionContainer>
  );
};
